import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput,
  Alert,
} from "@mui/material";
import AppModal from "../common/AppModal";
import AppButton from "../buttons/AppButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { receivePurchaseOrderItems } from "../../redux/actions/purchaseOrderActions";
import { getCompanyCharges } from "../../redux/actions/companyChargeActions";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { PurchaseOrderItemStatus } from "../../shared/enums/PurchaseOrderItemStatus";
import FormAlert from "../common/FormAlert";

interface ReceiveItemField {
  itemId: string;
  name: string;
  ordered: number;
  receivableQty: number;
  receivedQuantity: number;
  unitCost: number;
  status: PurchaseOrderItemStatus;
  checked: boolean;
}

const ReceivePoModal: React.FC<{
  open: boolean;
  onClose: () => void;
  order: PurchaseOrderResponseDto;
}> = ({ open, onClose, order }) => {
  const dispatch = useAppDispatch();
  const { loadingReceive, errorReceive } = useAppSelector(
    (state) => state.purchaseOrder,
  );
  const { items: companyCharges } = useAppSelector(
    (state) => state.companyCharge,
  );

  const [items, setItems] = useState<ReceiveItemField[]>(
    order.items
      .filter(
        (i) =>
          i.status === PurchaseOrderItemStatus.EXPECTED ||
          i.status === PurchaseOrderItemStatus.PARTIALLY_RECEIVED,
      )
      .map((i) => ({
        itemId: i.id,
        name: i.productName,
        ordered: i.orderedQuantity,
        receivableQty: i.orderedQuantity - i.receivedQuantity,
        receivedQuantity: i.orderedQuantity - i.receivedQuantity,
        unitCost: i.unitCost,
        status: PurchaseOrderItemStatus.RECEIVED,
        checked: false,
      })),
  );

  const [chargeIds, setChargeIds] = useState<string[]>([]);

  useEffect(() => {
    if (companyCharges.length === 0) dispatch(getCompanyCharges());
  }, [dispatch, companyCharges.length]);

  const handleReceive = (): void => {
    const payload = {
      items: items
        .filter((i) => i.checked)
        .map((i) => ({
          itemId: i.itemId,
          receivedQuantity: Number(i.receivedQuantity),
          unitCost: Number(i.unitCost),
          status: i.status,
        })),
      chargeIds,
    };
    dispatch(receivePurchaseOrderItems(order.id, payload));
  };

  return (
    <AppModal
      open={open}
      onClose={loadingReceive ? () => {} : onClose}
      title="Receive Goods"
      width="lg"
    >
      <FormAlert message={errorReceive} severity="error" />

      <Stack spacing={3}>
        <Alert severity="info">
          Confirm quantities and unit landing costs for this delivery batch.
        </Alert>

        <Box
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: "background.default" }}
                />
                <TableCell sx={{ bgcolor: "background.default" }}>
                  Product
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ bgcolor: "background.default" }}
                >
                  Receivable
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ bgcolor: "background.default" }}
                >
                  Qty Received
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: "background.default" }}>
                  Actual Unit Cost
                </TableCell>
                <TableCell sx={{ bgcolor: "background.default" }}>
                  Condition
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={item.itemId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={item.checked}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].checked = e.target.checked;
                        setItems(arr);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {item.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={item.receivableQty} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      disabled={!item.checked}
                      value={item.receivedQuantity}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].receivedQuantity = Number(e.target.value);
                        setItems(arr);
                      }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      disabled={!item.checked}
                      value={item.unitCost}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].unitCost = Number(e.target.value);
                        setItems(arr);
                      }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      disabled={!item.checked}
                      value={item.status}
                      onChange={(e) => {
                        const arr = [...items];
                        arr[idx].status = e.target
                          .value as PurchaseOrderItemStatus;
                        setItems(arr);
                      }}
                    >
                      <MenuItem value={PurchaseOrderItemStatus.RECEIVED}>
                        Good Condition
                      </MenuItem>
                      <MenuItem value={PurchaseOrderItemStatus.DAMAGED}>
                        Damaged
                      </MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Apply Shipping/Handling Charges</InputLabel>
          <Select
            multiple
            value={chargeIds}
            onChange={(e) => setChargeIds(e.target.value as string[])}
            input={<OutlinedInput label="Apply Shipping/Handling Charges" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(selected as string[]).map((id) => (
                  <Chip
                    key={id}
                    label={companyCharges.find((c) => c.id === id)?.name}
                    size="small"
                    onDelete={() =>
                      setChargeIds((prev) => prev.filter((id) => id !== id))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ))}
              </Box>
            )}
          >
            {companyCharges.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name} ({c.rate}%)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <AppButton
          variant="contained"
          color="primary"
          onClick={handleReceive}
          disabled={!items.some((i) => i.checked) || loadingReceive}
        >
          {loadingReceive
            ? "Processing..."
            : "Confirm Reception & Update Stock"}
        </AppButton>
      </Stack>
    </AppModal>
  );
};

export default ReceivePoModal;
